import { serverInfo, getServerConfigurationErrorMessage } from "../config/serverConfig";

import { apiFetch } from "./apiFetch";

/**
 * Exchange Firebase ID token for backend userInfo via /login/firebase.
 * Throws with a descriptive message if the backend fails or returns no userInfo,
 * so the UI can show why account/data could not be loaded.
 */
export async function exchangeFirebaseTokenForUserInfo(firebaseUser) {
  if (!firebaseUser) return null;

  const configErr = getServerConfigurationErrorMessage();
  if (configErr) {
    throw new Error(configErr);
  }

  let res;
  let data;
  try {
    // Force refresh so the backend gets a valid token (ID tokens expire in ~1 hour).
    const token = await firebaseUser.getIdToken(true);
    res = await apiFetch(serverInfo.serverUrl + "/login/firebase", {
      method: "POST",
      body: JSON.stringify({ idToken: token }),
    });
    const text = await res.text();
    data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
  } catch (err) {
    const msg = err && err.message === "Failed to fetch"
      ? "Server is unavailable or not responding. Check that the API is running and reachable."
      : (err && err.message) || "Could not reach server.";
    throw new Error(msg);
  }

  if (!res.ok) {
    const serverMessage = data && typeof data.message === "string" ? data.message : null;
    const byStatus = {
      400: serverMessage || "Invalid request.",
      401: serverMessage || "Invalid or expired session. Please log in again.",
      403: serverMessage || "Access denied. Please contact your administrator.",
      404: serverMessage || "User not found for this account.",
      503: serverMessage || "Server configuration error. Please try again later.",
    };
    throw new Error(byStatus[res.status] || serverMessage || `Server error (${res.status}). Please try again.`);
  }

  if (data && data.userInfo) {
    return data.userInfo;
  }

  throw new Error("Server did not return account information. Please log in again or contact support.");
}
